import { Button, Text } from '@react-spectrum/s2';
import ChevronRight from '@react-spectrum/s2/icons/ChevronRight';
import { Fragment, useCallback, useEffect, useState } from 'react';

import { PillChip } from '@/spaces/chat/PillChip';
import { pillTransitionName } from '@/spaces/chat/pillTransitionName';

import { TypewriterOnce } from '../components/TypewriterOnce';

import {
	buildStrategySummary,
	pillForSlot,
	type SlotId,
	type StrategyGoal,
	type SummaryParagraph,
} from './strategySummary';

const CHIP_STAGGER_MS = 140;
const CHIP_ENTER_MS = 360;

type StrategySummaryScreenProps = {
	goal: StrategyGoal | null;
	monthlyBudgetUsd: number;
	onContinue: (pillars: SlotId[]) => void;
};

const renderParagraph = (p: SummaryParagraph) => (
	<>
		{p.prefix}
		{p.bolds.map((b, i) => (
			<Fragment key={i}>
				<strong style={{ fontWeight: 700 }}>{b.label}</strong>
				{i < p.bolds.length - 1 ? (p.connectors[i] ?? '') : ''}
			</Fragment>
		))}
		{p.suffix}
	</>
);

export const StrategySummaryScreen = ({
	goal,
	monthlyBudgetUsd,
	onContinue,
}: StrategySummaryScreenProps) => {
	const summary = buildStrategySummary(goal, monthlyBudgetUsd);
	const [typingDone, setTypingDone] = useState(false);
	const handleTypingDone = useCallback(() => setTypingDone(true), []);
	const handleContinue = useCallback(
		() => onContinue(summary.pillars),
		[onContinue, summary.pillars],
	);

	const [chipsRevealed, setChipsRevealed] = useState(
		() =>
			typeof window !== 'undefined' &&
			typeof window.matchMedia === 'function' &&
			window.matchMedia('(prefers-reduced-motion: reduce)').matches,
	);
	useEffect(() => {
		if (chipsRevealed) return;
		const total =
			(summary.pillars.length - 1) * CHIP_STAGGER_MS + CHIP_ENTER_MS;
		const t = window.setTimeout(() => setChipsRevealed(true), total);
		return () => window.clearTimeout(t);
	}, [chipsRevealed, summary.pillars.length]);

	return (
		<div
			className="flex h-full w-full items-start justify-center overflow-y-auto
				py-12"
		>
			<div className="w-full max-w-[760px] px-8">
				<div
					className="mb-5"
					style={{
						fontSize: 11,
						fontWeight: 700,
						letterSpacing: '0.16em',
						color: '#6B7280',
					}}
				>
					YOUR STRATEGY
				</div>

				<div className="mb-8 flex flex-wrap" style={{ gap: 12 }}>
					{summary.pillars.map((slot, i) => {
						const pill = pillForSlot(slot);
						return (
							<div
								key={slot}
								className="animate-onb-chip-pop-in"
								style={{ animationDelay: `${i * CHIP_STAGGER_MS}ms` }}
							>
								<PillChip
									pill={pill}
									viewTransitionName={pillTransitionName(pill.label)}
								/>
							</div>
						);
					})}
				</div>

				<h2
					className="mb-8"
					style={{
						fontSize: 'clamp(26px, 4vw, 40px)',
						fontWeight: 700,
						letterSpacing: '-0.02em',
						lineHeight: 1.15,
						color: 'var(--text-primary)',
						minHeight: '1.15em',
					}}
				>
					{chipsRevealed && (
						<TypewriterOnce text={summary.opening} onDone={handleTypingDone} />
					)}
				</h2>

				<div
					style={{
						opacity: typingDone ? 1 : 0,
						transition: 'opacity 400ms ease',
						pointerEvents: typingDone ? 'auto' : 'none',
					}}
				>
					<div
						className="mb-8 flex flex-col"
						style={{
							gap: 18,
							fontSize: 17,
							lineHeight: 1.55,
							color: 'var(--text-primary)',
						}}
					>
						{summary.lead && <p>{renderParagraph(summary.lead)}</p>}
						{summary.support && <p>{renderParagraph(summary.support)}</p>}
						{summary.also && <p>{renderParagraph(summary.also)}</p>}
						{summary.skip && <p>{summary.skip}</p>}
						<p>{summary.close}</p>
					</div>

					<Button
						variant="primary"
						staticColor="black"
						fillStyle="fill"
						size="L"
						onPress={handleContinue}
					>
						<Text>Let&apos;s go</Text>
						<ChevronRight />
					</Button>
				</div>
			</div>
		</div>
	);
};
